import { useEffect, useState } from 'react'
import { leadStore } from './leadStore'
import { proposalStore } from '@/pages/Proposal/proposalStore'
import { agreementStore } from '@/pages/Agreement/agreementStore'
import { ProposalStatus } from '@/types'

/**
 * Distinct, sorted company names captured in Leads, optionally filtered by
 * lead type ('Generator' | 'Consumer') — and only once that lead has cleared
 * Rule §14: at least one APPROVED/WON proposal with a signed/completed
 * agreement against it. A freshly-created (or merely proposed) lead isn't a
 * real generator/consumer yet, so it shouldn't be selectable in those
 * masters until the deal is actually closed.
 *
 * Stays live via leadStore/proposalStore/agreementStore's change events, and
 * triggers a refresh on all three so the list is populated even when the
 * user lands directly on a master page without visiting those first.
 *
 * Used to suggest lead company names in the Generator / Consumer master forms.
 */
export function useLeadCompanies(type?: 'Generator' | 'Consumer'): string[] {
  const [companies, setCompanies] = useState<string[]>(() => collect(type))

  useEffect(() => {
    const update = () => setCompanies(collect(type))
    update()
    const unsubLeads = leadStore.subscribe(update)
    window.addEventListener(proposalStore.eventName, update)
    window.addEventListener(agreementStore.eventName, update)
    void leadStore.refresh()
    void proposalStore.refresh()
    void agreementStore.refresh()
    return () => {
      unsubLeads()
      window.removeEventListener(proposalStore.eventName, update)
      window.removeEventListener(agreementStore.eventName, update)
    }
  }, [type])

  return companies
}

// Same "signed" test GeneratorMaster/CustomerMaster's old Rule §14 picker used.
const isSignedAgreement = (a: { status?: string; signedAt?: any; signedCopyUrl?: string; signedCopies?: any[] }) =>
  a.status === 'Completed' || a.status === 'Active' || a.status === 'Executed' || a.status === 'Signed' ||
  !!a.signedAt || !!a.signedCopyUrl || ((a.signedCopies || []).length > 0)

// A lead has cleared Rule §14 once at least one of its proposals is
// APPROVED/WON and has a signed agreement raised against it.
function hasApprovedSignedDeal(leadId: string): boolean {
  const leadProposals = proposalStore.getAll().filter((p) => p.leadId === leadId)
  const approvedIds = new Set(
    leadProposals.filter((p) => p.status === ProposalStatus.APPROVED || p.status === ProposalStatus.WON).map((p) => p.id)
  )
  if (!approvedIds.size) return false
  return agreementStore.getAll().some((a) => approvedIds.has(a.proposalId) && isSignedAgreement(a))
}

function collect(type?: 'Generator' | 'Consumer'): string[] {
  const names = leadStore
    .getLeads()
    .filter((l) => (!type || l.leadType === type) && hasApprovedSignedDeal(l.id))
    .map((l) => (l.companyName || '').trim())
    .filter(Boolean)
  return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b))
}
